// Feb 2026 — FABTEXS AI Razorpay Checkout modal (TEST mode).
//
// The seller picks (tier, cycle); we call /api/billing/subscribe to create
// a Razorpay Subscription and then open Razorpay Checkout using the returned
// subscription_id. On payment success we hand the signature to
// /api/billing/verify. The webhook is authoritative — the UI shows a
// friendly "activating" state until subscription.activated arrives.
//
// The Razorpay Checkout.js script is loaded on demand.

import React, { useEffect, useState } from 'react';
import { X, Check, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(!!window.Razorpay));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const s = document.createElement('script');
    s.src = CHECKOUT_SRC;
    s.async = true;
    s.onload = () => resolve(!!window.Razorpay);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const TIERS = [
  { key: 'STANDARD', label: 'Standard' },
  { key: 'PRO', label: 'Pro' },
];

const CYCLES = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'yearly', label: 'Yearly (save 20%)' },
];

function CheckoutModal({ open, onClose, defaultTier = 'PRO', defaultCycle = 'monthly', onSubscribed }) {
  const [tier, setTier] = useState(defaultTier);
  const [cycle, setCycle] = useState(defaultCycle);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/billing/plans').then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, [open]);

  useEffect(() => { setTier(defaultTier); }, [defaultTier]);
  useEffect(() => { setCycle(defaultCycle); }, [defaultCycle]);

  if (!open) return null;

  const priceRow = plans.find((p) => p.tier === tier && p.cycle === cycle);
  const priceLabel = priceRow ? `₹${priceRow.amount_inr.toLocaleString('en-IN')}` : '—';
  const cycleLabel = cycle === 'monthly' ? '/month' : '/year';

  const handleSubscribe = async () => {
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) {
        toast.error('Could not load Razorpay Checkout. Please try again.');
        return;
      }
      const { data } = await api.post('/billing/subscribe', { tier, cycle });
      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: 'FABTEXS AI',
        description: `${tier} · ${cycle === 'monthly' ? 'Monthly' : 'Yearly'} subscription`,
        prefill: {
          name: data.seller?.name || '',
          email: data.seller?.email || '',
          contact: data.seller?.phone || '',
        },
        theme: { color: '#9333EA' },
        handler: async (resp) => {
          try {
            await api.post('/billing/verify', {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success('Payment authorized — your subscription is activating.');
            onSubscribed?.();
            onClose?.();
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Payment verification failed');
          }
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
        notes: { app: 'fabtexs-ai' },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error(resp?.error?.description || 'Payment failed. Please try again.');
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="checkout-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-fab-primary-lighter">
          <div className="font-display font-extrabold text-fab-text text-[15px]">Subscribe to FABTEXS AI</div>
          <button onClick={onClose} className="text-fab-text-muted hover:text-fab-text" data-testid="checkout-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1.5">Choose a plan</div>
            <div className="grid grid-cols-2 gap-2">
              {TIERS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTier(t.key)}
                  className={`px-3 py-2 rounded-lg border-2 text-[13px] font-semibold text-left transition-colors ${
                    tier === t.key
                      ? 'border-fab-primary bg-fab-primary-lighter/50 text-fab-primary'
                      : 'border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary/40'
                  }`}
                  data-testid={`checkout-tier-${t.key.toLowerCase()}`}
                >
                  <div className="flex items-center gap-1.5">
                    {tier === t.key && <Check className="w-3.5 h-3.5" />}
                    {t.label}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1.5">Billing cycle</div>
            <div className="grid grid-cols-2 gap-2">
              {CYCLES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCycle(c.key)}
                  className={`px-3 py-2 rounded-lg border-2 text-[12.5px] font-semibold transition-colors ${
                    cycle === c.key
                      ? 'border-fab-primary bg-fab-primary-lighter/50 text-fab-primary'
                      : 'border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary/40'
                  }`}
                  data-testid={`checkout-cycle-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-fab-primary-lighter p-3 flex items-baseline justify-between bg-fab-primary-lighter/20">
            <div className="text-[12px] text-fab-text-secondary">Total {cycle === 'monthly' ? 'per month' : 'per year'}</div>
            <div className="font-display font-extrabold text-fab-primary text-[20px]" data-testid="checkout-price">
              {priceLabel}<span className="text-[11.5px] text-fab-text-muted font-semibold ml-0.5">{cycleLabel}</span>
            </div>
          </div>

          <button
            onClick={handleSubscribe}
            disabled={busy}
            className="btn-primary w-full py-2.5 rounded-lg text-[13.5px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="checkout-pay-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {busy ? 'Opening Razorpay…' : 'Continue to secure payment'}
          </button>

          <div className="text-[10.5px] text-fab-text-muted text-center">
            Payments processed by Razorpay. Your subscription renews automatically each {cycle === 'monthly' ? 'month' : 'year'}.
            Cancel anytime from Settings → Plan &amp; Billing.
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutModal;
